import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { ReviewStatus } from 'models/Review';
import Admission from 'models/reviews/Admission';
import { updateReview } from 'services/api/reviews';
import colors from 'styles/theme/colors';

type DenyAdmissionModalProps = {
  onCancel: () => void;
  admission: Admission;
};

export default function DenyAdmissionModal({ onCancel, admission }: DenyAdmissionModalProps) {
  const [denialReason, setDenialReason] = useState<{ apiName: string; displayName: string }>();
  const { profile } = useProfile();
  const invalidateQueries = useInvalidateQueries();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: updateReview,
    onSuccess: () => {
      invalidateQueries({ admissions: true });
      onCancel();
    },
  });

  const handleSubmit = () => {
    mutate({
      id: admission.id,
      status: ReviewStatus.REJECTED,
      reviewedById: profile?.id,
      denialReasonApiName: denialReason?.apiName,
    });
  };

  return (
    <StandardModal title='Deny Referral' onCancel={onCancel} colorTheme={ColorThemeName.PrimaryRed}>
      <StandardModal.SubHeader>
        Provide a denial reason below to confirm the denial of this referral
      </StandardModal.SubHeader>

      <InputContainer>
        <label>Denial Reason</label>
        <Select
          options={admission.denialReasonOptions}
          onChange={(val) => setDenialReason(val)}
          getOptionValue={(prop) => prop.apiName}
          getOptionLabel={(prop) => prop.displayName}
          value={denialReason}
          placeholder='Denial Reason'
        />
      </InputContainer>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='danger' loading={isPending} disabled={!denialReason} onClick={handleSubmit}>
          Deny Referral
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 36px;

  label {
    color: ${colors.black75};
    font-size: 12px;
    margin-bottom: 4px;
  }
`;
